import { apiClient } from '@/lib/apiClient';

/** One managed application option (mirrors ApplicationListItem on the API). */
export interface ApplicationOption {
  id: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface SaveApplicationInput {
  name: string;
  sortOrder: number;
  isActive: boolean;
}

/**
 * Wrapper for the managed-application endpoints. The active list is readable by
 * any signed-in user (it backs the submit-ticket picker); the `all` list and
 * all mutations are IT-Admin-only on the server (403 otherwise).
 */
export const applicationsApi = {
  /** GET /api/applications — active options, ordered (the picker). */
  listActive: (
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<ApplicationOption[]> =>
    apiClient.get<ApplicationOption[]>('/api/applications', { getAccessToken, signal }),

  /** GET /api/applications/all — all options incl. inactive (admin manager). */
  listAll: (
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<ApplicationOption[]> =>
    apiClient.get<ApplicationOption[]>('/api/applications/all', { getAccessToken, signal }),

  /** POST /api/applications — add an option (admin). */
  create: (
    name: string,
    getAccessToken: () => Promise<string>
  ): Promise<ApplicationOption> =>
    apiClient.post<ApplicationOption>('/api/applications', { name }, { getAccessToken }),

  /** PUT /api/applications/{id} — rename / reorder / (de)activate (admin). */
  update: (
    id: string,
    input: SaveApplicationInput,
    getAccessToken: () => Promise<string>
  ): Promise<ApplicationOption> =>
    apiClient.put<ApplicationOption>(`/api/applications/${id}`, input, { getAccessToken }),

  /** DELETE /api/applications/{id} — remove an option (admin). */
  remove: (
    id: string,
    getAccessToken: () => Promise<string>
  ): Promise<void> =>
    apiClient.del<void>(`/api/applications/${id}`, { getAccessToken })
};
